import { StandardEvents } from '@shopify/events';
import {
  ATTRIBUTION_SESSION_KEY,
  ATTRIBUTION_STORAGE_KEY,
  blankPrivateAttributionAttributes,
  buildAttributionTouch,
  mergeAttributionState,
  privateAttributionAttributes,
} from '@theme/rbs-attribution-core';

let attributionState = null;
let processingAllowed = false;
let wasProcessingAllowed = false;
let lastWrittenSignature = '';
let writeQueue = Promise.resolve();

function safeStorageGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function safeStorageSet(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch (_error) {
    return false;
  }
}

function safeStorageRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_error) {
    // Storage can be unavailable in hardened browsers; fail closed without noise.
  }
}

function loadStoredState() {
  const raw = safeStorageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return mergeAttributionState(parsed, null);
  } catch (_error) {
    safeStorageRemove(window.localStorage, ATTRIBUTION_STORAGE_KEY);
    return null;
  }
}

function captureCurrentTouch() {
  const isSessionEntry = safeStorageGet(window.sessionStorage, ATTRIBUTION_SESSION_KEY) !== '1';
  const touch = buildAttributionTouch({
    locationHref: window.location.href,
    referrer: document.referrer,
    shopHost: window.location.hostname,
    isSessionEntry,
  });
  const previous = loadStoredState();
  const next = mergeAttributionState(previous, touch);
  if (touch) safeStorageSet(window.sessionStorage, ATTRIBUTION_SESSION_KEY, '1');
  if (next) safeStorageSet(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
  attributionState = next;
  return next;
}

function clearStoredState() {
  safeStorageRemove(window.localStorage, ATTRIBUTION_STORAGE_KEY);
  safeStorageRemove(window.sessionStorage, ATTRIBUTION_SESSION_KEY);
  attributionState = null;
  lastWrittenSignature = '';
}

function cartJsonUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root}cart.js`;
}

async function currentCartItemCount() {
  const response = await fetch(cartJsonUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart read failed: ${response.status}`);
  const cart = await response.json();
  return Number(cart?.item_count || 0);
}

async function writeCartAttributes(attributes, attempt = 0) {
  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ attributes }),
  });
  if (response.status === 409 && attempt === 0) {
    await new Promise((resolve) => window.setTimeout(resolve, 180));
    return writeCartAttributes(attributes, 1);
  }
  if (!response.ok) throw new Error(`cart attribution write failed: ${response.status}`);
  return response.json();
}

function enqueueAttributeWrite(attributes) {
  const signature = JSON.stringify(attributes);
  if (!Object.keys(attributes).length || signature === lastWrittenSignature) return writeQueue;
  writeQueue = writeQueue
    .catch(() => undefined)
    .then(() => writeCartAttributes(attributes))
    .then(() => {
      lastWrittenSignature = signature;
    });
  return writeQueue;
}

async function flushIfCartHasItems({ clear = false } = {}) {
  try {
    const itemCount = await currentCartItemCount();
    if (itemCount <= 0) {
      lastWrittenSignature = '';
      return;
    }
    const attributes = clear
      ? blankPrivateAttributionAttributes()
      : privateAttributionAttributes(attributionState);
    await enqueueAttributeWrite(attributes);
  } catch (_error) {
    // Attribution may never block browsing, cart, checkout, or consent changes.
  }
}

function marketingAttributionAllowed() {
  try {
    return window.Shopify?.customerPrivacy?.marketingAllowed() === true;
  } catch (_error) {
    return false;
  }
}

async function applyPrivacyState() {
  const allowedNow = marketingAttributionAllowed();
  const hadStoredState = Boolean(safeStorageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY));
  processingAllowed = allowedNow;
  if (allowedNow) {
    attributionState = captureCurrentTouch() || loadStoredState();
    if (attributionState) await flushIfCartHasItems();
  } else {
    clearStoredState();
    if (wasProcessingAllowed || hadStoredState) await flushIfCartHasItems({ clear: true });
  }
  wasProcessingAllowed = allowedNow;
}

function loadCustomerPrivacy() {
  if (window.Shopify?.customerPrivacy) return Promise.resolve(true);
  if (typeof window.Shopify?.loadFeatures !== 'function') return Promise.resolve(false);
  return new Promise((resolve) => {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => resolve(!error && Boolean(window.Shopify?.customerPrivacy))
    );
  });
}

document.addEventListener('visitorConsentCollected', () => {
  applyPrivacyState().catch(() => undefined);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!processingAllowed || !attributionState) return;
  if ('promise' in event && event.promise instanceof Promise) {
    event.promise
      .then(({ detail } = {}) => {
        if (!detail?.didError) return flushIfCartHasItems();
        return undefined;
      })
      .catch(() => undefined);
  }
});

loadCustomerPrivacy()
  .then((available) => {
    if (!available) {
      clearStoredState();
      return;
    }
    return applyPrivacyState();
  })
  .catch(() => {
    clearStoredState();
  });
